import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaArrowUp, FaArrowDown, FaMinus, FaInfoCircle } from 'react-icons/fa';

/**
 * Reusable glassmorphic KPI metric card for IAP Analytics.
 */
export default function MetricKpiCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  badge,
  tooltip,
  loading = false,
  color = 'indigo',
  onClick,
  testId = 'metric-kpi-card'
}) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [placement, setPlacement] = useState('right');
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (!isTooltipOpen) return;

    if (tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      const popoverWidth = 260;
      if (window.innerWidth < 640) {
        setPlacement('bottom');
      } else if (rect.right + popoverWidth + 20 > window.innerWidth) {
        setPlacement('left');
      } else {
        setPlacement('right');
      }
    }

    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setIsTooltipOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsTooltipOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTooltipOpen]);

  if (loading) {
    return (
      <div className={`metric-kpi-card is-loading card-color-${color}`} data-testid={`${testId}-loading`}>
        <div className="kpi-card-header">
          <div className="kpi-skeleton kpi-skeleton-icon" />
          <div className="kpi-skeleton kpi-skeleton-title" />
        </div>
        <div className="kpi-skeleton kpi-skeleton-value" />
        <div className="kpi-skeleton kpi-skeleton-subtitle" />
      </div>
    );
  }

  const renderTrend = () => {
    if (!trend) return null;
    const { direction = 'neutral', value: trendVal, label } = trend;
    let IconComponent = FaMinus;
    if (direction === 'up') IconComponent = FaArrowUp;
    if (direction === 'down') IconComponent = FaArrowDown;

    return (
      <span
        className={`kpi-trend-badge trend-${direction}`}
        title={label || `${direction} ${trendVal}`}
        data-testid="kpi-trend-badge"
      >
        <IconComponent className="trend-icon" />
        <span className="trend-val">{trendVal}</span>
        {label && <span className="trend-label">{label}</span>}
      </span>
    );
  };

  return (
    <div
      className={`metric-kpi-card card-color-${color} ${onClick ? 'is-clickable' : ''} ${isTooltipOpen ? 'has-open-tooltip' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      data-testid={testId}
    >
      <div className="kpi-card-header">
        <div className="kpi-icon-title-wrap">
          {icon && <div className="kpi-icon-wrap" aria-hidden="true">{icon}</div>}
          <span className="kpi-title">{title}</span>
        </div>
        {tooltip && (
          <div
            ref={tooltipRef}
            className="kpi-tooltip-container"
            onMouseEnter={() => setIsTooltipOpen(true)}
            onMouseLeave={() => setIsTooltipOpen(false)}
          >
            <button
              type="button"
              className={`kpi-info-btn ${isTooltipOpen ? 'is-active' : ''}`}
              aria-label={`About ${title}`}
              aria-expanded={isTooltipOpen}
              onClick={(e) => {
                e.stopPropagation();
                setIsTooltipOpen((prev) => !prev);
              }}
              data-testid="kpi-info-btn"
            >
              <FaInfoCircle className="kpi-info-icon" />
            </button>
            {isTooltipOpen && (
              <div
                className={`kpi-tooltip-popover placement-${placement}`}
                role="tooltip"
                onClick={(e) => e.stopPropagation()}
                data-testid="kpi-tooltip-popover"
              >
                <div className="kpi-tooltip-popover-header">
                  <strong>{title}</strong>
                </div>
                <div className="kpi-tooltip-popover-body">
                  {tooltip}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="kpi-card-body">
        <div className="kpi-value-row">
          <span className="kpi-value" data-testid="kpi-value">
            {value !== undefined && value !== null && value !== '' ? value : '—'}
          </span>
          {badge && <div className="kpi-badge-wrap">{badge}</div>}
        </div>

        {(subtitle || trend) && (
          <div className="kpi-footer-row">
            {renderTrend()}
            {subtitle && <span className="kpi-subtitle">{subtitle}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

MetricKpiCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitle: PropTypes.node,
  icon: PropTypes.node,
  trend: PropTypes.shape({
    direction: PropTypes.oneOf(['up', 'down', 'neutral']),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string
  }),
  badge: PropTypes.node,
  tooltip: PropTypes.string,
  loading: PropTypes.bool,
  color: PropTypes.oneOf(['indigo', 'emerald', 'violet', 'amber', 'blue', 'rose', 'slate']),
  onClick: PropTypes.func,
  testId: PropTypes.string
};
